import * as React from 'react';
import { superAdminGet } from 'Src/helpers/service/superadmin/api';

function downloadCsv(csvText: string, filename: string): void {
  const blob = new Blob([csvText], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function exportSchoolCsv(schoolId: string, type: 'students' | 'parents' | 'staff'): Promise<string> {
  return superAdminGet<string>(`/schools/${schoolId}/export/${type}`, { responseType: 'text' });
}

interface ExportItem {
  key: 'students' | 'parents' | 'staff';
  label: string;
  description: string;
  filename: string;
}

const EXPORTS: ExportItem[] = [
  {
    key: 'students',
    label: 'Export students',
    description: 'All students with form, house, medical info, next of kin.',
    filename: 'students.csv'
  },
  {
    key: 'parents',
    label: 'Export parents',
    description: 'All parents / next of kin linked to this school.',
    filename: 'parents.csv'
  },
  {
    key: 'staff',
    label: 'Export staff',
    description: 'All staff members with roles and forms.',
    filename: 'staff.csv'
  }
];

interface Props {
  schoolId: string;
}

interface State {
  loading: { [key: string]: boolean };
  errors: { [key: string]: string };
}

export class SuperAdminSchoolExports extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      loading: {},
      errors: {}
    };
  }

  onExportClick = (item: ExportItem) => {
    this.setState(prev => ({
      loading: { ...prev.loading, [item.key]: true },
      errors: { ...prev.errors, [item.key]: '' }
    }));

    exportSchoolCsv(this.props.schoolId, item.key)
      .then(csv => {
        downloadCsv(csv, item.filename);
        this.setState(prev => ({
          loading: { ...prev.loading, [item.key]: false }
        }));
      })
      .catch(err => {
        console.error(`Cannot export ${item.key}`, err);
        this.setState(prev => ({
          loading: { ...prev.loading, [item.key]: false },
          errors: { ...prev.errors, [item.key]: `Could not export ${item.key}. Please try again.` }
        }));
      });
  };

  render() {
    const { loading, errors } = this.state;

    return (
      <div>
        <div className="mb-3">
          <h4 className="mb-1">Exports</h4>
          <div className="text-muted small">Download school data as CSV files.</div>
        </div>

        <div className="list-group" style={{ maxWidth: 480 }}>
          {EXPORTS.map(item => {
            const isLoading = !!loading[item.key];
            const error = errors[item.key];
            return (
              <div key={item.key} className="list-group-item">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <div className="font-weight-bold">{item.label}</div>
                    <div className="text-muted small">{item.description}</div>
                    {error && <div className="text-danger small mt-1">{error}</div>}
                  </div>
                  <button
                    className="btn btn-outline-primary btn-sm ml-3"
                    onClick={() => this.onExportClick(item)}
                    disabled={isLoading}
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {isLoading ? 'Downloading…' : '⬇ Download CSV'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }
}
